import { useEffect, useId, useMemo, useRef, useState, type KeyboardEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

export interface SelectOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface SelectProps {
  id?: string;
  className?: string;
  'aria-label'?: string;
  'aria-labelledby'?: string;
  disabled?: boolean;
  invalid?: boolean;
  required?: boolean;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
  placeholder?: string;
  searchable?: boolean;
  searchPlaceholder?: string;
  emptyLabel?: string;
  maxMenuHeight?: number;
}

const TYPEAHEAD_RESET_MS = 700;
const MENU_GAP = 6;
const MENU_ROW_ESTIMATE = 44;

export function Select({
  id,
  className = '',
  'aria-label': ariaLabel,
  'aria-labelledby': ariaLabelledby,
  disabled = false,
  invalid = false,
  required = false,
  value,
  onChange,
  options,
  placeholder = '',
  searchable = false,
  searchPlaceholder,
  emptyLabel,
  maxMenuHeight = 288,
}: SelectProps) {
  const { t } = useTranslation();
  const resolvedSearchPlaceholder = searchPlaceholder ?? t('common.searchPlaceholder');
  const resolvedEmptyLabel = emptyLabel ?? t('common.noOptions');
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const listboxId = useId();
  const typeaheadRef = useRef({ buffer: '', at: 0 });
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number; width: number } | null>(null);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        (option.description ?? '').toLowerCase().includes(q),
    );
  }, [options, query]);

  const selected = options.find((option) => option.value === value);
  const controlSelector = searchable ? '.ui-selectControl' : '.ui-selectTrigger';

  const updateMenuPos = () => {
    const control = rootRef.current?.querySelector<HTMLElement>(controlSelector);
    if (!control) return;
    const rect = control.getBoundingClientRect();
    const estimated = Math.min(maxMenuHeight, filtered.length * MENU_ROW_ESTIMATE + 16);
    const spaceBelow = window.innerHeight - rect.bottom - MENU_GAP;
    const spaceAbove = rect.top - MENU_GAP;
    const flip = spaceBelow < Math.min(200, estimated) && spaceAbove > spaceBelow;
    setMenuPos(
      flip
        ? { top: Math.max(MENU_GAP, rect.top - estimated - MENU_GAP), left: rect.left, width: rect.width }
        : { top: rect.bottom + MENU_GAP, left: rect.left, width: rect.width },
    );
  };

  const close = (refocus = false) => {
    setOpen(false);
    setQuery('');
    setActiveIndex(-1);
    setMenuPos(null);
    if (refocus) triggerRef.current?.focus();
  };

  const openMenu = () => {
    if (disabled) return;
    setOpen(true);
    setQuery('');
    setActiveIndex(Math.max(0, options.findIndex((option) => option.value === value)));
    requestAnimationFrame(() => {
      updateMenuPos();
      if (searchable) searchRef.current?.focus();
    });
  };

  const choose = (option: SelectOption) => {
    if (option.disabled) return;
    onChange(option.value);
    close(true);
  };

  const nextSelectable = (from: number, direction: 1 | -1): number => {
    let index = from;
    for (let step = 0; step < filtered.length; step += 1) {
      if (index < 0 || index >= filtered.length) return -1;
      if (!filtered[index].disabled) return index;
      index += direction;
    }
    return -1;
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (disabled) return;
    const typingInSearch = searchable && open && (event.target as HTMLElement).tagName === 'INPUT';

    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      const direction = event.key === 'ArrowDown' ? 1 : -1;
      const base = activeIndex < 0 ? (direction === 1 ? -1 : filtered.length) : activeIndex + direction;
      const next = nextSelectable(base, direction);
      if (next >= 0) setActiveIndex(next);
      return;
    }

    if (event.key === 'Home' || event.key === 'End') {
      event.preventDefault();
      if (!open) return;
      const next = nextSelectable(event.key === 'Home' ? 0 : filtered.length - 1, event.key === 'Home' ? 1 : -1);
      if (next >= 0) setActiveIndex(next);
      return;
    }

    if (event.key === 'Enter' || event.key === ' ') {
      if (typingInSearch && event.key === ' ') return;
      event.preventDefault();
      if (!open) {
        openMenu();
        return;
      }
      if (event.key === 'Enter' && activeIndex >= 0) {
        const option = filtered[activeIndex];
        if (option && !option.disabled) choose(option);
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      if (open) close(true);
      return;
    }

    if (event.key === 'Tab') {
      if (open) close(false);
      return;
    }

    if (!typingInSearch && event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      const now = Date.now();
      const typeahead = typeaheadRef.current;
      if (now - typeahead.at > TYPEAHEAD_RESET_MS) typeahead.buffer = '';
      typeahead.buffer += event.key.toLowerCase();
      typeahead.at = now;
      const start = open && activeIndex >= 0 ? activeIndex + 1 : 0;
      const target = (start + filtered.findIndex((option, index) => {
        if (index < start) return false;
        return !option.disabled && option.label.toLowerCase().startsWith(typeahead.buffer);
      })) % filtered.length;
      if (target >= 0) {
        if (open) setActiveIndex(target);
        else if (!options[target].disabled) choose(options[target]);
      }
    }
  };

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      const target = event.target as Node;
      if (rootRef.current?.contains(target)) return;
      if (listRef.current?.contains(target)) return;
      close(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    updateMenuPos();
    window.addEventListener('resize', updateMenuPos);
    window.addEventListener('scroll', updateMenuPos, true);
    return () => {
      window.removeEventListener('resize', updateMenuPos);
      window.removeEventListener('scroll', updateMenuPos, true);
    };
  }, [open, query, filtered.length]);

  useEffect(() => {
    if (!open) return;
    const el = listRef.current?.querySelector<HTMLElement>(`[data-opt="${activeIndex}"]`);
    el?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, open]);

  return (
    <div ref={rootRef} className={`ui-select${className ? ` ${className}` : ''}`} onKeyDown={handleKeyDown}>
      {open && searchable ? (
        <div className="ui-selectControl">
          <svg
            className="ui-selectSearchIcon"
            viewBox="0 0 24 24"
            width="16"
            height="16"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={searchRef}
            className="ui-selectSearchInput"
            role="combobox"
            aria-label={ariaLabel}
            aria-labelledby={ariaLabelledby}
            aria-expanded="true"
            aria-controls={listboxId}
            aria-activedescendant={activeIndex >= 0 ? `ui-select-option-${listboxId}-${activeIndex}` : undefined}
            placeholder={resolvedSearchPlaceholder}
            value={query}
            onChange={(event) => {
              const nextQuery = event.target.value;
              setQuery(nextQuery);
              const q = nextQuery.trim().toLowerCase();
              const first = options.findIndex(
                (option) => !option.disabled && (option.label.toLowerCase().includes(q) || (option.description ?? '').toLowerCase().includes(q)),
              );
              setActiveIndex(first);
            }}
          />
        </div>
      ) : (
        <button
          ref={triggerRef}
          id={id}
          type="button"
          className={`ui-selectTrigger${invalid ? ' ui-selectTrigger--invalid' : ''}`}
          aria-label={ariaLabel}
          aria-labelledby={ariaLabelledby}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listboxId}
          aria-required={required || undefined}
          aria-invalid={invalid || undefined}
          disabled={disabled}
          onClick={() => (open ? close(true) : openMenu())}
        >
          <span className={`ui-selectValue${selected ? '' : ' ui-selectValue--placeholder'}`}>
            {selected ? selected.label : placeholder || value}
          </span>
          <svg
            className={`ui-selectChevron${open ? ' ui-selectChevron--open' : ''}`}
            viewBox="0 0 24 24"
            width="18"
            height="18"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
      )}

      {open &&
        menuPos &&
        createPortal(
          <ul
            ref={listRef}
            id={listboxId}
            role="listbox"
            className="ui-selectMenu"
            style={{ top: menuPos.top, left: menuPos.left, width: menuPos.width, maxHeight: maxMenuHeight }}
          >
            {filtered.length === 0 ? (
              <li role="option" aria-disabled="true" className="ui-selectEmpty">
                {resolvedEmptyLabel}
              </li>
            ) : (
              filtered.map((option, index) => {
                const isSelected = option.value === value;
                const isActive = index === activeIndex;
                return (
                  <li
                    key={option.value}
                    role="option"
                    id={`ui-select-option-${listboxId}-${index}`}
                    data-opt={index}
                    aria-selected={isSelected}
                    aria-disabled={option.disabled || undefined}
                    className={`ui-selectOption${isActive ? ' ui-selectOption--active' : ''}${isSelected ? ' ui-selectOption--selected' : ''}${option.disabled ? ' ui-selectOption--disabled' : ''}`}
                    onMouseEnter={() => setActiveIndex(index)}
                    onClick={() => choose(option)}
                  >
                    <span className="ui-selectOptionText">
                      <span className="ui-selectOptionLabel">{option.label}</span>
                      {option.description && <span className="ui-selectOptionDescription">{option.description}</span>}
                    </span>
                    {isSelected && (
                      <svg
                        className="ui-selectOptionCheck"
                        viewBox="0 0 24 24"
                        width="16"
                        height="16"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        aria-hidden="true"
                      >
                        <polyline points="20 6 9 17 4 12" />
                      </svg>
                    )}
                  </li>
                );
              })
            )}
          </ul>,
          document.body,
        )}
    </div>
  );
}
